import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Mail,
  Copy,
  CheckCircle2,
  AlertCircle,
  ExternalLink,
  Calendar,
  Clock,
  Info
} from 'lucide-react';
import { authFetch } from '@/utils/auth-fetch';

interface Interview {
  id: string;
  expert_name: string;
  expert_email?: string;
  share_link?: string;
  token_expires_at?: string;
}

interface InterviewInviteModalProps {
  interview: Interview;
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export function InterviewInviteModal({
  interview,
  open,
  onClose,
  onSuccess
}: InterviewInviteModalProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [shareLink, setShareLink] = useState(interview.share_link || '');
  const [linkCopied, setLinkCopied] = useState(false);

  // Email form state
  const [emailTo, setEmailTo] = useState(interview.expert_email || '');
  const [emailMessage, setEmailMessage] = useState('');
  const [expiryDays, setExpiryDays] = useState('7');

  useEffect(() => {
    if (open && !shareLink) {
      generateLink();
    }
  }, [open]);

  const generateLink = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await authFetch(
        `/api/interviews/${interview.id}/generate-token`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ expiresInDays: parseInt(expiryDays) })
        }
      );

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to generate link');
      }

      const data = await response.json();
      setShareLink(data.shareLink);
    } catch (err) {
      console.error('Error generating link:', err);
      setError(err instanceof Error ? err.message : 'Failed to generate link');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareLink);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
      setError('Failed to copy link to clipboard');
    }
  };

  const handleSendEmail = async () => {
    if (!emailTo) {
      setError('Email address is required');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await authFetch(
        `/api/interviews/${interview.id}/send-invitation`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: emailTo,
            customMessage: emailMessage
          })
        }
      );

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to send invitation');
      }

      setSuccess('Invitation email sent successfully!');
      setTimeout(() => {
        onSuccess();
        onClose();
      }, 1500);
    } catch (err) {
      console.error('Error sending email:', err);
      setError(err instanceof Error ? err.message : 'Failed to send email');
    } finally {
      setLoading(false);
    }
  };

  const expiryDate = interview.token_expires_at
    ? new Date(interview.token_expires_at)
    : null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Send Interview Invitation</DialogTitle>
          <DialogDescription>
            Invite {interview.expert_name} to prepare and record the interview
          </DialogDescription>
        </DialogHeader>

        {/* Error/Success Messages */}
        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {success && (
          <Alert className="bg-green-50 border-green-200">
            <CheckCircle2 className="h-4 w-4 text-green-600" />
            <AlertDescription className="text-green-800">{success}</AlertDescription>
          </Alert>
        )}

        {/* Tabs */}
        <Tabs defaultValue="email" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="email">
              <Mail className="w-4 h-4 mr-2" />
              Send Email
            </TabsTrigger>
            <TabsTrigger value="copy">
              <Copy className="w-4 h-4 mr-2" />
              Copy Link
            </TabsTrigger>
          </TabsList>

          {/* Email Tab */}
          <TabsContent value="email" className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email_to">Recipient Email *</Label>
              <Input
                id="email_to"
                type="email"
                placeholder="expert@example.com"
                value={emailTo}
                onChange={(e) => setEmailTo(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email_message">Custom Message (Optional)</Label>
              <Textarea
                id="email_message"
                placeholder="Add a personal message to the expert..."
                value={emailMessage}
                onChange={(e) => setEmailMessage(e.target.value)}
                rows={4}
              />
              <p className="text-xs text-muted-foreground">
                This will be included in the invitation email
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="expiry_days">Link Expiry</Label>
              <div className="flex items-center gap-2">
                <Input
                  id="expiry_days"
                  type="number"
                  min="1"
                  max="30"
                  value={expiryDays}
                  onChange={(e) => setExpiryDays(e.target.value)}
                  className="w-24"
                />
                <span className="text-sm text-muted-foreground">days</span>
              </div>
            </div>

            {expiryDate && (
              <Alert>
                <Calendar className="h-4 w-4" />
                <AlertDescription>
                  Link expires on: <strong>{expiryDate.toLocaleDateString()}</strong> at{' '}
                  <strong>{expiryDate.toLocaleTimeString()}</strong>
                </AlertDescription>
              </Alert>
            )}

            <Button
              onClick={handleSendEmail}
              disabled={!emailTo || loading}
              className="w-full"
            >
              <Mail className="w-4 h-4 mr-2" />
              {loading ? 'Sending...' : 'Send Invitation Email'}
            </Button>
          </TabsContent>

          {/* Copy Link Tab */}
          <TabsContent value="copy" className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="share_link">Interview Link</Label>
              <div className="flex gap-2">
                <Input
                  id="share_link"
                  value={shareLink}
                  readOnly
                  className="flex-1 font-mono text-sm"
                />
                <Button
                  variant="outline"
                  onClick={handleCopyLink}
                  disabled={!shareLink}
                >
                  {linkCopied ? (
                    <>
                      <CheckCircle2 className="w-4 h-4 mr-2" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4 mr-2" />
                      Copy
                    </>
                  )}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Share this link with the expert via your preferred communication method
              </p>
            </div>

            {shareLink && (
              <>
                <Alert>
                  <Info className="h-4 w-4" />
                  <AlertDescription>
                    The expert will use this link to access the interview preparation flow
                    and record the interview when ready.
                  </AlertDescription>
                </Alert>

                <Button
                  variant="outline"
                  onClick={() => window.open(shareLink, '_blank')}
                  className="w-full"
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Preview Expert View
                </Button>
              </>
            )}

            {!shareLink && (
              <Button onClick={generateLink} disabled={loading} className="w-full">
                {loading ? 'Generating...' : 'Generate New Link'}
              </Button>
            )}
          </TabsContent>
        </Tabs>

        {/* Footer Info */}
        <div className="text-xs text-muted-foreground space-y-1">
          <p>
            <strong>What happens next:</strong>
          </p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li>Expert receives invitation with briefing document</li>
            <li>Expert clicks secure link to access preparation flow</li>
            <li>4-screen guided setup (intro → briefing → setup → record)</li>
            <li>Expert records interview when ready</li>
            <li>Transcript automatically generated and processed</li>
          </ul>
        </div>
      </DialogContent>
    </Dialog>
  );
}
